import type { Font } from 'opentype.js';

/**
 * Host-provided chalk rendering assets. The rich glyph-outline ChalkWriter needs
 * real TTF bytes (parsed with opentype.js) plus an optional pencil image. A tsup
 * library can't bundle binary fonts the way a webpack app can, so the host
 * injects them — exactly like the TtsBridge. Absent → ChalkWriter falls back to
 * the lightweight character-stream reveal (no opentype, no fonts needed).
 */
export interface ChalkAssetsPort {
  /** TTF URL for an authored family/weight, or null to skip glyph rendering. */
  fontUrl(family: string | undefined, weight: number | undefined): string | null;
  /** Optional pencil image URL that rides the active glyph's tip. */
  pencilUrl?: string;
}

let port: ChalkAssetsPort | null = null;

/** Inject the host's chalk fonts/pencil (call once at startup). null → off. */
export function setChalkAssets(p: ChalkAssetsPort | null): void {
  port = p;
}

export function chalkFontUrl(
  family: string | undefined,
  weight: number | undefined,
): string | null {
  return port ? port.fontUrl(family, weight) : null;
}

export function chalkPencilUrl(): string | undefined {
  return port?.pencilUrl;
}

// Parsed fonts cached by URL (each TTF fetched + parsed at most once).
const cache = new Map<string, Promise<Font>>();

/** Fetch + parse a TTF into an opentype `Font` (opentype is lazily imported so
 *  hosts that never use glyph-chalk don't pull it in). */
export function loadChalkFont(url: string): Promise<Font> {
  const hit = cache.get(url);
  if (hit) return hit;
  const p = (async () => {
    const ot = (await import('opentype.js')).default;
    const buf = await (await fetch(url)).arrayBuffer();
    return ot.parse(buf) as unknown as Font;
  })();
  cache.set(url, p);
  return p;
}

// Chalk TTFs registered as CSS fonts, by URL → synthetic family name.
const faceCache = new Map<string, Promise<string | null>>();

/**
 * Register the chalk TTF as a CSS `FontFace` under a synthetic family name and
 * resolve that name (null when FontFace/document is unavailable or the load
 * fails). Handwriting layout MUST measure text with the SAME font the glyph
 * outlines are drawn from — measuring with the element's authored family while
 * drawing chalk glyphs spaces the letters at a foreign font's advances.
 */
export function loadChalkFace(url: string): Promise<string | null> {
  const hit = faceCache.get(url);
  if (hit) return hit;
  const p = (async () => {
    if (typeof FontFace === 'undefined' || typeof document === 'undefined') return null;
    try {
      const family = `__ai-tutor-chalk-${faceCache.size}`;
      const face = new FontFace(family, `url("${url}")`);
      await face.load();
      document.fonts.add(face);
      return family;
    } catch {
      return null;
    }
  })();
  faceCache.set(url, p);
  return p;
}
